<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowLeft,
  ChevronRight,
  CircleUserRound,
  Compass,
  Download,
  Home,
  LayoutDashboard,
  ListTree,
  LogIn,
  Map,
  PlusCircle,
  ShieldCheck,
} from '@lucide/vue'
import { auth, hasCreatorAccess } from '../state'
import { standaloneMode } from '../api'

defineProps<{ compact?: boolean; immersive?: boolean }>()

type PageInfo = {
  title: string
  description: string
  section?: string
  sectionTo?: string
  fallback: string
}

const route = useRoute()
const router = useRouter()
const mapPanel = ref<HTMLDetailsElement | null>(null)
const localCreatorRoute = computed(() => standaloneMode || Boolean(route.meta.localCreator))

const pageInfo = computed<PageInfo>(() => {
  switch (route.name) {
    case 'home': return { title: '首页', description: '网站总览与主要入口', fallback: '/' }
    case 'explore': return { title: '心光馆与量域馆', description: '浏览站长审核的精选问卷、分类与主题搜索', fallback: '/' }
    case 'survey-detail': return { title: '问卷详情', description: '了解内容说明、参与条件与风险提示', section: '心光量域', sectionTo: '/explore', fallback: '/explore' }
    case 'survey-take': return { title: '填写问卷', description: '沉浸作答，当前答案会自动保存', section: '问卷详情', sectionTo: `/surveys/${route.params.slug}`, fallback: `/surveys/${route.params.slug}` }
    case 'result': return { title: '结果报告', description: '查看得分、结果角色与成长建议', section: '心光量域', sectionTo: '/explore', fallback: '/explore' }
    case 'login': return { title: '站长 / 管理员入口', description: '仅用于平台审核、发布与安全管理', fallback: '/' }
    case 'local-create': return { title: '在线本地创作台', description: '导入、编辑和答卷回收只保存在当前浏览器，不上传服务器', fallback: '/' }
    case 'local-creator-new': return { title: '新建本地问卷', description: '草稿将自动保存在当前浏览器', section: '本地创作台', sectionTo: '/local-create', fallback: '/local-create' }
    case 'local-creator-edit': return { title: '编辑本地问卷', description: '修改只保存到当前浏览器', section: '本地创作台', sectionTo: '/local-create', fallback: '/local-create' }
    case 'local-creator-analytics': return { title: '本地问卷数据', description: '查看浏览器内保存的答卷与导出备份', section: '本地创作台', sectionTo: '/local-create', fallback: '/local-create' }
    case 'creator': return standaloneMode
      ? { title: '本地创作台', description: '问卷和答卷仅保存在当前浏览器', fallback: '/creator' }
      : { title: '内容工作台', description: '管理员导入、复核、发布与回收问卷', fallback: '/' }
    case 'creator-new': return { title: '新建问卷', description: standaloneMode ? '内容仅保存在当前浏览器' : '设计问卷并完成发布前复核', section: standaloneMode ? '本地创作台' : '内容工作台', sectionTo: '/creator', fallback: '/creator' }
    case 'creator-edit': return { title: '编辑问卷', description: '修改草稿并保留历史版本与回答', section: standaloneMode ? '本地创作台' : '内容工作台', sectionTo: '/creator', fallback: '/creator' }
    case 'creator-analytics': return { title: '问卷数据中心', description: '查看分析、管理答卷质量和导出', section: standaloneMode ? '本地创作台' : '内容工作台', sectionTo: '/creator', fallback: '/creator' }
    case 'account': return { title: '管理员安全设置', description: '更换密码、开启两步验证并管理登录设备', fallback: '/admin' }
    case 'account-action': return { title: '账号操作', description: '完成账号验证、密码重置或数据操作', section: '账号中心', sectionTo: '/account', fallback: '/account' }
    case 'admin': return { title: '站长管理与审核', description: '管理后台账号、人工审核并处理风险内容', fallback: '/' }
    default: return { title: '页面未找到', description: '这个地址暂时没有对应内容', fallback: '/' }
  }
})

const isHome = computed(() => route.name === 'home')
const isAdmin = computed(() => ['admin', 'safety_admin', 'super_admin'].includes(auth.user?.role || ''))

const featureLinks = computed(() => [
  { to: '/', label: '首页', help: '平台介绍与主要入口', icon: Home, show: true },
  { to: '/explore', label: '心光馆 · 量域馆', help: '站长精选、分类、搜索与问卷', icon: Compass, show: true },
  { to: '/local-create', label: '在线本地创作', help: '无需登录，数据只保存在自己的浏览器', icon: Download, show: !standaloneMode && (!auth.user || localCreatorRoute.value) },
  { to: '/creator', label: '内容工作台', help: '管理员导入、复核并管理问卷数据', icon: LayoutDashboard, show: hasCreatorAccess() },
  { to: '/creator/new', label: '新建问卷', help: '由管理员创建并完成发布前复核', icon: PlusCircle, show: hasCreatorAccess() },
  { to: '/account', label: '管理员安全设置', help: '密码、两步验证与登录设备', icon: CircleUserRound, show: !standaloneMode && Boolean(auth.user) },
  { to: '/admin', label: '站长审核台', help: '创作者邀请、人工审核与风险治理', icon: ShieldCheck, show: isAdmin.value },
  { to: '/login', label: '站长 / 管理员入口', help: '仅供获授权的后台管理人员', icon: LogIn, show: !standaloneMode && !auth.user },
].filter((item) => item.show))

function goBack() {
  const previous = router.options.history.state.back
  if (typeof previous === 'string' && previous) router.back()
  else void router.push(pageInfo.value.fallback)
}

function closeMap() {
  if (mapPanel.value) mapPanel.value.open = false
}
</script>

<template>
  <section :class="['context-navigation', { compact, immersive }]" aria-label="当前位置与功能导航">
    <div class="context-navigation-inner">
      <button v-if="!isHome" class="context-back" type="button" aria-label="返回上一个界面" title="返回上一个界面" @click="goBack">
        <ArrowLeft />
        <span>返回</span>
      </button>
      <span v-else class="context-origin" aria-label="当前位于首页"><Home /><span>起点</span></span>

      <nav class="breadcrumb" aria-label="面包屑导航">
        <RouterLink v-if="!isHome" to="/">首页</RouterLink>
        <ChevronRight v-if="!isHome" />
        <template v-if="pageInfo.section">
          <RouterLink :to="pageInfo.sectionTo || '/'">{{ pageInfo.section }}</RouterLink>
          <ChevronRight />
        </template>
        <strong>{{ pageInfo.title }}</strong>
      </nav>

      <div class="context-copy">
        <span>{{ pageInfo.title }}</span>
        <small>{{ pageInfo.description }}</small>
      </div>

      <details ref="mapPanel" class="feature-map">
        <summary aria-label="打开全部功能导航" title="全部功能"><Map /><span>全部功能</span><ChevronRight class="feature-map-chevron" /></summary>
        <div class="feature-map-panel">
          <div class="feature-map-heading"><ListTree /><span><b>功能地图</b><small>从这里前往任意主要区域</small></span></div>
          <RouterLink v-for="item in featureLinks" :key="item.to" :to="item.to" @click="closeMap">
            <component :is="item.icon" />
            <span><b>{{ item.label }}</b><small>{{ item.help }}</small></span>
            <ChevronRight />
          </RouterLink>
        </div>
      </details>
    </div>
  </section>
</template>
